import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MuiCheckbox from "./MuiCheckbox";
import { fetchUserWishlistIds, updateWishlist } from "api/Wishlist";
import { fetchProductList } from "api/Product";
import { useDebounce } from "hooks/useDebounce";
import { Pagination } from "antd";
import { addItemToCart } from "api/Cart";
import { useWishlistContext } from "providers/WishlistProvider";
import RightQualityControl from "providers/QualityControlComponent/RightQualityControl";

interface Product {
    id: string;
    name: string;
    img: string;
    seller: string;
    sellerCategoryId: number | string;
    maxQty: number;
    checked: boolean;
    qty: number;
    slug: string;
    price: number;
    oldPrice?: number;
    promoPrice?: number;
    discount?: string;
    hasPromotion: boolean;
    promotionName?: string;
}

export default function WishlistList() {
    const { refreshWishlist } = useWishlistContext();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [wishlistIds, setWishlistIds] = useState<number[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [page, setPage] = useState<number>(1);
    const [totalItems, setTotalItems] = useState<number>(0);
    const LIMIT = 10;

    const totalChecked = products.filter(p => p.checked).length;
    const allChecked = totalChecked === products.length && products.length > 0;
    const indeterminate = totalChecked > 0 && totalChecked < products.length;

    const toggleCheckItem = (id: string) => {
        setProducts(prev =>
            prev.map(p =>
                p.id === id ? { ...p, checked: !p.checked } : p
            )
        );
    };

    const toggleCheckAll = () => {
        const target = !(allChecked && !indeterminate);
        setProducts(prev => prev.map(p => ({ ...p, checked: target })));
    };

    const changeQty = async (id: string, value: number, change: number) => {
        const res = await addItemToCart(id, change);

        setProducts((prev) =>
            prev.map((p) =>
                p.id === id
                    ? { ...p, qty: Math.max(0, Math.min(value, p.maxQty)) }
                    : p
            )
        );
    };

    useEffect(() => {
        const initWishlist = async () => {
            const ids = await fetchUserWishlistIds();
            setWishlistIds(ids);
        };
        initWishlist();
    }, []);

    useEffect(() => {
        const loadProducts = async () => {
            setLoading(true);
            try {
                // Nếu không có ID nào trong wishlist thì clear luôn
                if (wishlistIds.length === 0) {
                    setProducts([]);
                    setTotalItems(0);
                    setLoading(false);
                    return;
                }

                const res = await fetchProductList({
                    list_id: wishlistIds.join(','),
                    page: page,
                    limit: LIMIT,
                    name: debouncedSearchTerm,
                    sort: ['created_at,desc']
                });

                if (res.status && res.data && res.data.items) {
                    setTotalItems(res.data.total);

                    const mappedProducts: Product[] = res.data.items.map((item: any) => {
                        const originalPrice = Number(item.price) || 0;
                        const apiDiscountPrice = Number(item.discount_price) || 0;
                        const promotionPercent = Number(item.promotion?.percent) || 0;

                        const computedDiscountPrice = apiDiscountPrice > 0
                            ? apiDiscountPrice
                            : (promotionPercent > 0 ? Math.round(originalPrice * (1 - promotionPercent / 100)) : originalPrice);

                        const finalDiscountPrice = computedDiscountPrice > 0 && computedDiscountPrice < originalPrice
                            ? computedDiscountPrice
                            : originalPrice;

                        const finalDiscountPercent = originalPrice > 0 && finalDiscountPrice < originalPrice
                            ? Math.round((1 - finalDiscountPrice / originalPrice) * 100)
                            : 0;

                        return {
                            id: String(item.id),
                            name: item.name,
                            img: item.avatar || "/theme/placeholder.svg",
                            seller: item.product_category?.name || "Dược Tâm Hạnh",
                            sellerCategoryId: item.product_category?.id || "",
                            maxQty: item.is_stock ? 100 : 0,
                            checked: false,
                            qty: 0,
                            slug: item.slug,
                            price: finalDiscountPrice,
                            oldPrice: finalDiscountPercent > 0 ? originalPrice : undefined,
                            promoPrice: finalDiscountPercent > 0 ? finalDiscountPrice : undefined,
                            discount: finalDiscountPercent > 0 ? `-${finalDiscountPercent}%` : undefined,
                            hasPromotion: !!item.promotion,
                            promotionName: item.promotion?.detail?.name || undefined,
                        };
                    });

                    setProducts(mappedProducts);
                } else {
                    setProducts([]);
                    setTotalItems(0);
                }
            } catch (error) {
                console.error("Lỗi tải sản phẩm", error);
            } finally {
                setLoading(false);
            }
        };

        loadProducts();
    }, [wishlistIds, page, debouncedSearchTerm]);

    const formatCurrency = (amount: number) => {
        return amount.toLocaleString('vi-VN') + 'đ';
    };

    // Hàm xóa nhiều (Checkbox)
    const deleteSelected = async () => {
        setLoading(true);
        const selectedIds = products.filter(p => p.checked).map(p => Number(p.id));
        if (selectedIds.length === 0) return;

        for (const id of selectedIds) {
            await updateWishlist(id);
        }

        // Cập nhật lại local state ID
        const newIds = wishlistIds.filter(id => !selectedIds.includes(id));
        setWishlistIds(newIds);
        setLoading(false);

        // Đồng bộ lại WishlistProvider để các component khác hiển thị đúng
        refreshWishlist();

        // Reset checkbox
        if (products.filter(p => !p.checked).length === 0 && page > 1) {
            setPage(prev => prev - 1);
        }
    };

    return (
        <div className="MuiGrid-root MuiGrid-container MuiGrid-direction-xs-column">
            <header className="MuiPaper-root MuiAppBar-root MuiAppBar-positionFixed MuiAppBar-colorPrimary styles_appBar__Kzodx mui-fixed MuiPaper-elevation4 w-full-1199-24px show-1199">
                <div className="MuiToolbar-root MuiToolbar-regular styles_toolBar__DQTee MuiToolbar-gutters">
                    <Link to="/" className="MuiButtonBase-root MuiButton-root MuiButton-text styles_btnHamburger__POqzB" aria-label="back pagehome">
                        <span className="MuiButton-label">
                            <span style={{ boxSizing: "border-box", display: "inline-block", overflow: "hidden", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px", position: "relative", maxWidth: "100%" }}>
                                <span style={{ boxSizing: "border-box", display: "block", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px", maxWidth: "100%" }}>
                                    <img alt="" aria-hidden="true" src="data:image/svg+xml,%3csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20version=%271.1%27%20width=%2724%27%20height=%2724%27/%3e" style={{ display: "block", maxWidth: "100%", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px" }} />
                                </span>
                                <img src="/theme/arrow-left.svg" decoding="async" data-nimg="intrinsic" style={{ position: "absolute", inset: "0px", boxSizing: "border-box", padding: "0px", border: "none", margin: "auto", display: "block", width: "0px", height: "0px", minWidth: "100%", maxWidth: "100%", minHeight: "100%", maxHeight: "100%" }} />
                            </span>
                        </span>
                        <span className="MuiTouchRipple-root"></span>
                    </Link>
                    <p className="MuiTypography-root styles_formTitle__o8AZx MuiTypography-body1">Sản phẩm quan tâm</p>
                </div>
            </header>
            <div className="styles_searchWrapper__1VsnB show-1199">
                <div className="MuiInputBase-root MuiInput-root styles_root_input__fUHE5 MuiInputBase-adornedStart">
                    <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-1a5basm" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="SearchIcon">
                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
                    </svg>
                    <input placeholder="Nhập tên thuốc, hoạt chất cần tìm" type="search" className="MuiInputBase-input MuiInput-input styles_input__p8e0Z MuiInputBase-inputAdornedStart MuiInputBase-inputTypeSearch MuiInput-inputTypeSearch" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }} />
                </div>
            </div>
            <div className="style_wrapperContent__9ddkL">

                {/* ==== TOP BAR ==== */}
                <div className="style_containerTop__j5JB3">
                    <span className="style_titleContent__NuXwE">
                        Danh sách sản phẩm bạn quan tâm
                    </span>

                    <div className="style_rightContent__ql1ZG">

                        {/* Delete */}
                        <button
                            type="button"
                            className="MuiButtonBase-root style_buttonDelete__qgtWb style_check__aDusd"
                            onClick={deleteSelected}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M7.22866 14.3957C7.47053 14.3957 7.67683 14.3094 7.84756 14.1367C8.01829 13.9496 8.10366 13.7338 8.10366 13.4892V8.09352C8.0752 7.8777 7.97561 7.69784 7.80488 7.55396C7.63415 7.39568 7.43496 7.31655 7.20732 7.31655C6.9939 7.31655 6.80183 7.39568 6.6311 7.55396C6.46037 7.69784 6.36077 7.8777 6.33232 8.09352V13.4892C6.33232 13.7338 6.41768 13.9496 6.58841 14.1367C6.77337 14.3094 6.98679 14.3957 7.22866 14.3957ZM16.1067 3.60432H12.564V2.69784C12.564 2.20863 12.4431 1.76259 12.2012 1.35971C11.9594 0.942445 11.6321 0.61151 11.2195 0.366906C10.8211 0.122302 10.3801 0 9.89634 0H8.10366C7.61992 0 7.17175 0.122302 6.75915 0.366906C6.36077 0.61151 6.04065 0.942445 5.79878 1.35971C5.55691 1.76259 5.43598 2.20863 5.43598 2.69784V3.60432H1.89329C1.67988 3.63309 1.49492 3.73381 1.33841 3.90647C1.19614 4.07914 1.125 4.28058 1.125 4.51079C1.125 4.72662 1.19614 4.92086 1.33841 5.09352C1.49492 5.26619 1.67988 5.36691 1.89329 5.39568H2.76829V15.3022C2.76829 15.7914 2.88923 16.2374 3.1311 16.6403C3.37297 17.0576 3.69309 17.3885 4.09146 17.6331C4.50407 17.8777 4.95224 18 5.43598 18H12.564C13.0478 18 13.4888 17.8777 13.8872 17.6331C14.2998 17.3885 14.627 17.0576 14.8689 16.6403C15.1108 16.2374 15.2317 15.7914 15.2317 15.3022V5.39568H16.1067C16.3201 5.36691 16.498 5.26619 16.6402 5.09352C16.7967 4.92086 16.875 4.72662 16.875 4.51079C16.875 4.28058 16.7967 4.07914 16.6402 3.90647C16.498 3.73381 16.3201 3.63309 16.1067 3.60432ZM7.22866 2.69784C7.22866 2.45324 7.31402 2.2446 7.48476 2.07194C7.65549 1.89928 7.86179 1.81295 8.10366 1.81295H9.89634C10.1382 1.81295 10.3445 1.89928 10.5152 2.07194C10.686 2.2446 10.7713 2.45324 10.7713 2.69784V3.60432H7.22866V2.69784ZM13.439 15.3022C13.439 15.5468 13.3537 15.7554 13.1829 15.9281C13.0122 16.1007 12.8059 16.187 12.564 16.187H5.43598C5.19411 16.187 4.9878 16.1007 4.81707 15.9281C4.64634 15.7554 4.56098 15.5468 4.56098 15.3022V5.39568H13.439V15.3022ZM10.7713 14.3957C11.0132 14.3957 11.2195 14.3094 11.3902 14.1367C11.5752 13.9496 11.6677 13.7338 11.6677 13.4892V8.09352C11.6392 7.8777 11.5396 7.69784 11.3689 7.55396C11.1982 7.39568 10.999 7.31655 10.7713 7.31655C10.5579 7.31655 10.3659 7.39568 10.1951 7.55396C10.0244 7.69784 9.9248 7.8777 9.89634 8.09352V13.4892C9.89634 13.7338 9.98171 13.9496 10.1524 14.1367C10.3232 14.3094 10.5295 14.3957 10.7713 14.3957Z" fill="#428c04"></path></svg>
                            <span>Xóa khỏi danh sách</span>
                        </button>

                        {/* Select all */}
                        <div
                            className="style_wrapperCheckbox__sBg1i style_check__aDusd"
                            onClick={toggleCheckAll}
                        >
                            <span>Chọn tất cả</span>
                            <MuiCheckbox
                                checked={allChecked}
                                indeterminate={indeterminate}
                                onChange={toggleCheckAll}
                            />
                        </div>
                    </div>
                </div>

                {/* ==== SEARCH ==== */}

                <div className="MuiFormControl-root MuiTextField-root style_root_input__8oRc3 style_root_input_search__RBL1w hidden-1199">
                    <div className="MuiInputBase-root MuiInput-root MuiInput-underline MuiInputBase-formControl MuiInput-formControl MuiInputBase-adornedStart">
                        <div className="MuiInputAdornment-root style_search_ic__W9sB1 MuiInputAdornment-positionStart">
                            <span style={{ boxSizing: "border-box", display: "inline-block", overflow: "hidden", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px", position: "relative", maxWidth: "100%" }}>
                                <span style={{ boxSizing: "border-box", display: "block", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px", maxWidth: "100%" }}>
                                    <img alt="" aria-hidden="true" src="data:image/svg+xml,%3csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20version=%271.1%27%20width=%2718%27%20height=%2718%27/%3e" style={{ display: "block", maxWidth: "100%", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px" }} />
                                </span>
                                <img src="/theme/SearchActive_icon.svg" decoding="async" data-nimg="intrinsic" style={{ position: "absolute", inset: "0px", boxSizing: "border-box", padding: "0px", border: "none", margin: "auto", display: "block", width: "0px", height: "0px", minWidth: "100%", maxWidth: "100%", minHeight: "100%", maxHeight: "100%" }} />
                            </span>
                        </div>
                        <input
                            placeholder="Nhập tên thuốc, hoạt chất cần tìm"
                            type="search"
                            className="MuiInputBase-input MuiInput-input styles_input__p8e0Z"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPage(1);
                            }}
                        />
                    </div>
                </div>

                {/* ==== PRODUCT LIST ==== */}
                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center' }}>Đang tải dữ liệu...</div>
                ) : products.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center' }}>
                        {searchTerm ? 'Không tìm thấy sản phẩm nào khớp với từ khóa.' : 'Danh sách yêu thích trống.'}
                    </div>
                ) : (
                    <>
                        {products.map((p) => (
                            <div key={p.id} className="style_wrapperCard__yyX_D">
                                <div className="styles_cardContainer__roWvH style_cardItem__rKGSb">
                                    <div className="styles_flex__YOT7Y">
                                        <Link to={`/product/${p.slug}`}>
                                            <div className="styles_wrapperImage__vkqUl">
                                                <img src={p.img} alt={p.name} />
                                            </div>
                                        </Link>
                                        <div className="styles_wrapperInfoProduct__zIAXY">
                                            <Link className="styles_name__JFs3F" to={`/product/${p.slug}`} style={{ textDecoration: "none", color: "inherit" }}>{p.name}</Link>
                                            <div className="styles_sellerContainer__zCB_8">
                                                <span style={{ boxSizing: "border-box", display: "inline-block", overflow: "hidden", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px", position: "relative", maxWidth: "100%" }}>
                                                    <span style={{ boxSizing: "border-box", display: "block", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px", maxWidth: "100%" }}>
                                                        <img alt="" aria-hidden="true" src="data:image/svg+xml,%3csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20version=%271.1%27%20width=%2713%27%20height=%2713%27/%3e" style={{ display: "block", maxWidth: "100%", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px" }} />
                                                    </span>
                                                    <img src="/theme/seller_store.svg" decoding="async" data-nimg="intrinsic" style={{ position: "absolute", inset: "0px", boxSizing: "border-box", padding: "0px", border: "none", margin: "auto", display: "block", width: "0px", height: "0px", minWidth: "100%", maxWidth: "100%", minHeight: "100%", maxHeight: "100%" }} />
                                                </span>
                                                <Link className="styles_cardNameStore__T1gCP" to={p.sellerCategoryId ? `/products?category=${p.sellerCategoryId}` : "#"}>{p.seller}</Link>
                                            </div>
                                            {p.hasPromotion && (
                                                <div className="styles_cardBadges__umsyP MuiBox-root css-0">
                                                    <Link className="styles_mobile_rootBase__8z7PQ" to="/deals">
                                                        <img alt="tag-/deals" src="/theme/Tag_Label_IconText-5.svg" title="Khuyến Mãi" />
                                                    </Link>
                                                    {p.promotionName && (
                                                        <span style={{ fontSize: 12, color: "#d32f2f", marginLeft: 4 }}>{p.promotionName}</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="styles_wrapperContent__K5zzD">
                                        {p.oldPrice && p.discount && (
                                            <div className="styles_wrapperDiscount__H_zPT">
                                                <p className="MuiTypography-root styles_promo__Q5YiA MuiTypography-body1"> {p.discount}</p>
                                                <Link className="styles_cardDiscount__HubGJ" to={`/product/${p.slug}`}>
                                                    <div className="styles_flex_no_wrap__1vvft MuiBox-root css-0">
                                                        <div className="styles_canvasPrice__vw932 text-[15px] line-through text-[#333333]" style={{ height: "25px" }}>{formatCurrency(p.oldPrice)}</div>
                                                    </div>
                                                </Link>
                                            </div>
                                        )}
                                        <div>
                                            <Link to={`/product/${p.slug}`}>
                                                <div className="styles_flex_no_wrap__1vvft MuiBox-root css-0">
                                                    <div className="styles_canvasPrice__vw932 text-[22px] text-[#428c04]" style={{ height: "34px" }}>{formatCurrency(p.price)}</div>
                                                </div>
                                            </Link>
                                        </div>
                                    </div>

                                    {/* Quantity Control */}
                                    <div className="styles_wrapperQuantityControl__LHcc9">
                                        <span className="styles_cardNoted__fnyQc d-none">Đặt tối đa {p.maxQty} sản phẩm</span>
                                        <RightQualityControl productId={Number(p.id)} productName={p.name} />
                                    </div>
                                </div>
                                <MuiCheckbox checked={p.checked} indeterminate={false} onChange={() => toggleCheckItem(p.id)} />
                            </div>
                        ))}

                        {/* PAGINATION */}
                        {totalItems > LIMIT && (
                            <div className="flex justify-center mt-4 mb-8">
                                <Pagination
                                    current={page}
                                    total={totalItems}
                                    pageSize={LIMIT}
                                    onChange={(newPage) => setPage(newPage)}
                                    showSizeChanger={false}
                                />
                            </div>
                        )}
                    </>)}
                <span className="style_finishScroll__8c9dA">
                    Đã hết số lượng sản phẩm quan tâm
                </span>
            </div>
        </div>
    );
}
